import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { map, Observable, tap } from 'rxjs';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { getDomainNameFromHostname } from '@rp/utils';

import { ACCESS_COOKIE, ACCESS_TOKEN, REFRESH_COOKIE, REFRESH_TOKEN } from '../constants';
import { PersonalInfo, SignInPayload, SignInResponse } from '../models';
import { AuthBaseService } from './auth-base.service';

@Injectable({ providedIn: 'root' })
export class AdminAuthService extends AuthBaseService {
  private readonly _router = inject(Router);
  private readonly _cookieService = inject(CookieService);

  signIn(payload: SignInPayload): Observable<void> {
    return this.http
      .post<SignInResponse>(`${this.adminApiHost}/admin/auth/token/login/`, payload)
      .pipe(
        map(({ access, refresh }) => {
          this.localStorageService.setValue<string>(ACCESS_TOKEN, access);
          this.localStorageService.setValue<string>(REFRESH_TOKEN, refresh);

          this._router.navigate(['/']);
        }),
      );
  }

  getPersonalInfo(): Observable<PersonalInfo> {
    return this.http
      .get<PersonalInfo>(`${this.adminApiHost}/admin/users/personal-info/`)
      .pipe(tap(personalInfo => this.setPersonalInfoToStorage(personalInfo)));
  }

  loginAsManager(id: number, url: string): Observable<{ access: string; refresh: string }> {
    const cookieOptions: CookieOptions = {
      sameSite: 'None',
      path: '/',
      domain: `.${getDomainNameFromHostname(location.hostname)}`,
      secure: true,
    };

    return this.http
      .post<{
        access: string;
        refresh: string;
      }>(`${this.adminApiHost}/admin/auth/token/login-as-user/`, { impersonatedUserId: id })
      .pipe(
        tap(({ access, refresh }) => {
          this._cookieService.set(ACCESS_COOKIE, access, cookieOptions);
          this._cookieService.set(REFRESH_COOKIE, refresh, cookieOptions);

          const link = document.createElement('a');

          link.setAttribute('target', '_blank');
          link.setAttribute('href', url);
          link.click();
        }),
      );
  }
}
